import {customElement, property} from 'lit/decorators.js';
import {CSSResult, html, LitElement, nothing, PropertyValues, unsafeCSS} from 'lit';
import {map} from 'lit/directives/map.js';
import {consume} from '@lit/context';
import {collectionsContext, productsContext} from '../../../scripts/context/wishlistContext.js';
import {CollectionsState, ProductsState} from '../../../scripts/context/wishlist-state.js';
import {WishlistService} from '../../../scripts/service/wishlist-service.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {Messages, Product, WishlistCollection} from '../../../@types/wishlist.js';
import './wishlist-item-slider-item.js';
import '../carousel/index.js';
import {sharedCss} from '../../../scripts/style-bundle/shared.js';
import styles from './wishlist-item-slider.scss?inline';
import arrowLeft from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_chevron-left.svg?element';
import arrowRight from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_chevron-right.svg?element';
import {createRef, ref, Ref} from 'lit/directives/ref.js';
import type {Carousel} from '@breuni-ui/carousel';
import {WishlistAppConfig, wishlistAppConfigContext} from '../../../scripts/context/appConfigContext.js';
import {collectionApi} from '../../../scripts/service/collection-api.js';
import {classMap} from 'lit/directives/class-map.js';
import {hideIconFromAssistiveTechnology} from '../icon/icon-accessibility.js';

@customElement('ents-wishlist-item-slider')
export class WishlistItemSlider extends LitElement {
  @consume({context: productsContext, subscribe: true})
  @property({attribute: false})
  private productsState!: ProductsState;

  @consume({context: collectionsContext, subscribe: true})
  @property({attribute: false})
  private collectionsState!: CollectionsState;

  @consume({context: wishlistAppConfigContext})
  @property({attribute: false})
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({context: i18nContext})
  @property({attribute: false})
  private messages!: Messages;

  @property({type: Object, attribute: 'collection'})
  private wishlistCollection?: WishlistCollection;

  @property({type: Boolean, attribute: 'is-empty-collection'})
  private isEmptyCollection: boolean = false;

  private carouselRef: Ref<Carousel> = createRef();

  private scrollBottom?: number = undefined;

  connectedCallback() {
    super.connectedCallback();
  }

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  private addProductToCollection(e: CustomEvent<{ collectionId: string, product: Product }>) {
    this.storeScrollPosition();
    collectionApi.addItem(
      this.wishlistAppConfig.collectionEndpoint,
      e.detail.collectionId,
      e.detail.product.syan,
      e.detail.product.colorId
    ).then((res) => {
      if (res.isOk && res.collection) {
        this.productsState.addToCollection(e.detail.product, res.collection, 'collection_detail');
        this.collectionsState.putToTop(res.collection);
      }
    });
  }

  private removeProductFromCollection(e: CustomEvent<{ collectionId: string, product: Product }>) {
    this.storeScrollPosition();
    collectionApi.deleteItem(
      this.wishlistAppConfig.collectionEndpoint,
      e.detail.collectionId,
      e.detail.product.syan,
      e.detail.product.colorId
    ).then((res) => {
      if (res.isOk && res.collection) {
        this.productsState.removeFromCollection(e.detail.product, res.collection, 'collection_detail');
      }
    });
  }

  private storeScrollPosition() {
    this.scrollBottom = document.documentElement.offsetHeight - document.documentElement.scrollTop;
  }

  private restoreScrollPosition() {
    if (this.scrollBottom !== undefined) {
      document.documentElement.scrollTop = document.documentElement.offsetHeight - this.scrollBottom;
      this.scrollBottom = undefined;
    }
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    this.carouselRef.value?.firstUpdated();
    setTimeout(() => {
      this.restoreScrollPosition();
    });
  }

  protected render() {
    const products = WishlistService.getProductsWithCollectionStatusSortedByDefaultSorting(
      this.productsState.entities,
      this.wishlistCollection?.id
    );
    if (products.length == 0 || this.wishlistCollection === undefined) {
      return nothing;
    }
    return html`
      <ents-carousel-container
        carousel-selector="ents-carousel"
        navigation-next-selector="ents-carousel-navi-next"
        navigation-prev-selector="ents-carousel-navi-prev"
        data-test-ents-wishlist-item-slider
      >
        <header slot="header">
          <div class="container-center carousel-header">
            <div class="carousel-header--heading ${classMap({'sr-only': this.isEmptyCollection})}">
              <h2 class="ents-heading">${this.messages['ents_wishlist_collection_slider_headline']}</h2>
            </div>
            <div class="carousel-header--navigation">
              <ents-carousel-navi-prev>
                <button class="bdk-button-icon" size="extra-small" variant="outline">
                  ${hideIconFromAssistiveTechnology(arrowLeft())}
                  <span class="sr-only">${this.messages['ents_wishlist_collection_slider_previous']}</span>
                </button>
              </ents-carousel-navi-prev>
              <ents-carousel-navi-next>
                <button class="bdk-button-icon" size="extra-small" variant="outline">
                  ${hideIconFromAssistiveTechnology(arrowRight())}
                  <span class="sr-only">${this.messages['ents_wishlist_collection_slider_next']}</span>
                </button>
              </ents-carousel-navi-next>
            </div>
          </div>
        </header>
        <div slot="body">
          <ents-carousel ${ref(this.carouselRef)}>
            ${map(
              products,
              product => html`
                <ents-wishlist-item-slider-item
                  .product=${product}
                  .collectionId="${this.wishlistCollection?.id}"
                  ?is-in-collection="${product.isInCollection}"
                  @add-product-to-collection="${this.addProductToCollection}"
                  @remove-product-from-collection="${this.removeProductFromCollection}"
                >
                </ents-wishlist-item-slider-item>`
            )}
          </ents-carousel>
        </div>
      </ents-carousel-container>`;
  }
}
